import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import PropTypes from 'prop-types';
import { customValidators, object, string } from '../../../Helpers/Validators/customValidators';
import { FormControl, FormLabel } from '@mui/material';
import { CheckboxInput, RadioInput, StyledRadioInput, TextInput } from "../../../Helpers/Inputs";
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import Age from '../../../Helpers/Dates/Age';
import Step5Icon from '../icons/step_5.svg?react';
import IconHolder from "../../../Helpers/IconHolder";
import { getLabelForAids, getLabelForDiabetes, getLabelForMedicalIssues, nicotineUsageRadios } from "../Helpers/MedicalHistoryData";
import { InputMaskHeight, InputMaskWeight } from "../../../Helpers/Formatters";

export default function MedicalHistory(props) {
  const { register, setValue, trigger } = props;

  const noneOfTheAboveDisabled = (
    props.formState.alcoholOrDrugAbuse === "true" ||
    props.formState.heartDisease === "true" ||
    props.formState.stroke === "true" ||
    props.formState.lungDisease === "true" ||
    props.formState.depressionBipolar === "true" ||
    props.formState.cognitiveDisorder === "true" ||
    props.formState.muscularDystrophy === "true" ||
    props.formState.kidneyLiverDisease === "true" ||
    props.formState.leukemia === "true" ||
    props.formState.arthritis === "true" ||
    props.formState.sleepApnea === "true"
  );

  const state = props.basicInfo.state;
  const insuredAge = Age(props.basicInfo.birthdate);

  // uncheck "none" if it becomes disabled
  useEffect(() => {
    if (noneOfTheAboveDisabled && props.formState.none === "true") {
      props.onFieldChange('none', "false");
    } else if (noneOfTheAboveDisabled && props.formState.none !== "true") {
      trigger('none');
    }
  }, [noneOfTheAboveDisabled]);

  useEffect(() => {
    register('insuredAge');
    setValue('insuredAge', insuredAge);
  }, [register]);

  useEffect(() => {
    if (props.formState.useRecreationalMarijuana !== "true") {
      props.onFieldChange('marijuanaFrequency', "");
    }
  }, [props.formState.useRecreationalMarijuana]);

  const handleValueChange = (field, value) => {
    if (field === 'nicotineUse') {
      const form = { ...props.fullFormState };
      form.steps.basicInfo.tobacco = value;
      form.steps.medicalHistory.nicotineUse = value;
      props.onMultiFieldChange(form);
      trigger(field);
    }
    else {
      props.onFieldChange(field, value);
      trigger(field);
    }
  };

  const handleCheckBoxValue = (field, value) => {
    props.onFieldChange(field, ConvertBooleanToString(value));
  };

  return (
    <React.Fragment>
      <Row className="justify-content-center">
        <IconHolder icon={Step5Icon} viewBox="0 0 25 25" color='primary' />
      </Row>
      <h4 className="headerTitle">Reviewing Your Medical History</h4>
      <h5 className="headerSubtitle">This information replaces taking a medical exam.</h5>
      <div className="mb-3">
        <RadioInput
          name="nicotineUse"
          id="nicotineUse"
          label="When was the last time you used any nicotine products, including electronic cigarettes or vaping devices?"
          value={props.formState.nicotineUse}
          radios={nicotineUsageRadios}
          onChange={e => handleValueChange('nicotineUse', e.target.value)}
        />
      </div>

      {props.showHeightAndWeight &&
        <Row>
          <Col>
            <div className="mb-3">
              <TextInput
                name="height"
                id="height"
                label="Height"
                value={props.formState.height}
                onChange={(e) => handleValueChange('height', e.target.value)}
                InputProps={{
                  inputComponent: InputMaskHeight,
                }}
              />
            </div>
          </Col>

          <Col>
            <div className="mb-3">
              <TextInput
                name="weight"
                id="weight"
                label="Weight"
                value={props.formState.weight}
                onChange={(e) => handleValueChange('weight', e.target.value)}
                InputProps={{
                  inputComponent: InputMaskWeight,
                }}
              />
            </div>
          </Col>
        </Row>
      }

      <div className="mb-3">
        <StyledRadioInput
          name="useRecreationalMarijuana"
          id="useRecreationalMarijuana"
          label="Do you use Marijuana recreationally?"
          value={props.formState.useRecreationalMarijuana}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('useRecreationalMarijuana', e)}
        />
      </div>

      {props.formState.useRecreationalMarijuana === "true" &&
        <div className="mb-3">
          <RadioInput
            name="marijuanaFrequency"
            id="marijuanaFrequency"
            label="What is the average frequency of your use?"
            value={props.formState.marijuanaFrequency}
            radios={[{ value: "Once a week", label: "Once a week" }, { value: "2-3 times a week", label: "2-3 times a week" }, { value: "More than 3 times a week", label: "More than 3 times a week" }]}
            onChange={e => handleValueChange('marijuanaFrequency', e.target.value)}
          />
        </div>
      }

      <div className="mb-3">
        <StyledRadioInput
          name="illegalDrugUse"
          id="illegalDrugUse"
          label="In the past 5 years, have you used cocaine, opiates, methamphetamines or any illegal drug?"
          value={props.formState.illegalDrugUse}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('illegalDrugUse', e)}
        />
      </div>

      <div className="mb-3">
        <StyledRadioInput
          name="illegalPrescriptionDrugUse"
          id="illegalPrescriptionDrugUse"
          label="In the past 5 years, have you used prescription drugs other than as prescribed by a licensed member of the medical profession?"
          value={props.formState.illegalPrescriptionDrugUse}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('illegalPrescriptionDrugUse', e)}
        />
      </div>

      <FormControl component="fieldset">
        <FormLabel component="legend">{getLabelForMedicalIssues(state)}:</FormLabel>
        <div className="checkboxes mb-3">
          <CheckboxInput
            name="alcoholOrDrugAbuse"
            id="alcoholOrDrugAbuse"
            checked={ConvertStringToBoolean(props.formState.alcoholOrDrugAbuse)}
            onChange={e => handleCheckBoxValue('alcoholOrDrugAbuse', e.target.checked)}
            label="Alcohol or drug abuse?"
          />
          <CheckboxInput
            name="heartDisease"
            id="heartDisease"
            checked={ConvertStringToBoolean(props.formState.heartDisease)}
            onChange={e => handleCheckBoxValue('heartDisease', e.target.checked)}
            label="Heart disease, heart surgery, heart failure, irregular heart rate?"
          />
          <CheckboxInput
            name="stroke"
            id="stroke"
            checked={ConvertStringToBoolean(props.formState.stroke)}
            onChange={e => handleCheckBoxValue('stroke', e.target.checked)}
            label="Stroke, mini-stroke, aneurysm?"
          />
          <CheckboxInput
            name="lungDisease"
            id="lungDisease"
            checked={ConvertStringToBoolean(props.formState.lungDisease)}
            onChange={e => handleCheckBoxValue('lungDisease', e.target.checked)}
            label="Chronic lung disease, emphysema, chronic bronchitis, cystic fibrosis, asthma requiring a daily steroid?"
          />
          <CheckboxInput
            name="depressionBipolar"
            id="depressionBipolar"
            checked={ConvertStringToBoolean(props.formState.depressionBipolar)}
            onChange={e => handleCheckBoxValue('depressionBipolar', e.target.checked)}
            label="Major depression, bipolar, schizophrenia, eating disorder?"
          />
          <CheckboxInput
            name="cognitiveDisorder"
            id="cognitiveDisorder"
            checked={ConvertStringToBoolean(props.formState.cognitiveDisorder)}
            onChange={e => handleCheckBoxValue('cognitiveDisorder', e.target.checked)}
            label="Cognitive disorder, Alzheimer’s, dementia, Parkinson’s, amyotrophic lateral sclerosis (ALS), seizure disorder?"
          />
          <CheckboxInput
            name="muscularDystrophy"
            id="muscularDystrophy"
            checked={ConvertStringToBoolean(props.formState.muscularDystrophy)}
            onChange={e => handleCheckBoxValue('muscularDystrophy', e.target.checked)}
            label="Muscular dystrophy, multiple sclerosis, cerebral palsy, paralysis?"
          />
          <CheckboxInput
            name="kidneyLiverDisease"
            id="kidneyLiverDisease"
            checked={ConvertStringToBoolean(props.formState.kidneyLiverDisease)}
            onChange={e => handleCheckBoxValue('kidneyLiverDisease', e.target.checked)}
            label="Kidney disease, liver disease, blood disorder?"
          />
          <CheckboxInput
            name="leukemia"
            id="leukemia"
            checked={ConvertStringToBoolean(props.formState.leukemia)}
            onChange={e => handleCheckBoxValue('leukemia', e.target.checked)}
            label="Leukemia, melanoma, brain tumor or cancer other than basal cell skin cancer (BCC)?"
          />
          <CheckboxInput
            name="arthritis"
            id="arthritis"
            checked={ConvertStringToBoolean(props.formState.arthritis)}
            onChange={e => handleCheckBoxValue('arthritis', e.target.checked)}
            label="Rheumatoid arthritis, lupus, scleroderma?"
          />
          <CheckboxInput
            name="sleepApnea"
            id="sleepApnea"
            checked={ConvertStringToBoolean(props.formState.sleepApnea)}
            onChange={e => handleCheckBoxValue('sleepApnea', e.target.checked)}
            label="Sleep apnea, which is not treated by CPAP or BiPAP therapy?"
          />
          <CheckboxInput
            name="none"
            id="none"
            checked={ConvertStringToBoolean(props.formState.none)}
            onChange={e => handleCheckBoxValue('none', e.target.checked)}
            label="None of the above"
            disabled={noneOfTheAboveDisabled}
          />
        </div>
      </FormControl>

      <div className="mb-3">
        <StyledRadioInput
          name="diabetes"
          id="diabetes"
          label={getLabelForDiabetes(state)}
          value={props.formState.diabetes}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('diabetes', e)}
        />
      </div>

      {props.formState.diabetes === "true" &&
        <React.Fragment>
          <div className="mb-3">
            <StyledRadioInput
              name="diabetesPriorToAge40"
              id="diabetesPriorToAge40"
              label="Was it diagnosed prior to age 40?"
              value={props.formState.diabetesPriorToAge40}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('diabetesPriorToAge40', e)}
            />
          </div>

          <div className="mb-3">
            <StyledRadioInput
              name="insulinDependent"
              id="insulinDependent"
              label="Are you insulin dependent?"
              value={props.formState.insulinDependent}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('insulinDependent', e)}
            />
          </div>

          <div className="mb-3">
            <StyledRadioInput
              name="complicationsfromDiabetes"
              id="complicationsfromDiabetes"
              label="Have you had diabetic complications such as nerve pain, skin ulcers, vision, or kidney problems?"
              value={props.formState.complicationsfromDiabetes}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('complicationsfromDiabetes', e)}
            />
          </div>
        </React.Fragment>
      }

      <div className="mb-3">
        <StyledRadioInput
          name="aids"
          id="aids"
          label={getLabelForAids(state)}
          value={props.formState.aids}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('aids', e)}
        />
      </div>

      <div className="mb-3">
        <StyledRadioInput
          name="incompleteFollowup"
          id="incompleteFollowup"
          label="In the past 2 years, has a licensed member of the medical profession recommended additional follow-up treatment which has not been completed yet?"
          value={props.formState.incompleteFollowup}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('incompleteFollowup', e)}
        />
      </div>
    </React.Fragment>
  );
}

MedicalHistory.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  basicInfo: PropTypes.object,
  fullFormState: PropTypes.object,
  register: PropTypes.func,
  trigger: PropTypes.func,
  onMultiFieldChange: PropTypes.func,
  setValue: PropTypes.func
};

MedicalHistory.ValidationSchema = (showHeightAndWeight = false) => object().shape({
  nicotineUse: string().required('This field is required.'),
  height: showHeightAndWeight ? customValidators.string()
    .required('Height is required.')
    .isValidEtradeHeightString('Invalid height', 36, 84) : string().notRequired(),
  weight: showHeightAndWeight ? customValidators.string()
    .required('Weight is required.')
    .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395) : string().notRequired(),
  useRecreationalMarijuana: string().required('This field is required.'),
  marijuanaFrequency: string().when("useRecreationalMarijuana", {
    is: "true",
    then: (schema) => schema.required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  illegalDrugUse: string().required('This field is required.'),
  illegalPrescriptionDrugUse: string().required('This field is required.'),

  alcoholOrDrugAbuse: string().required('This field is required.'),
  heartDisease: string().required('This field is required.'),
  stroke: string().required('This field is required.'),
  lungDisease: string().required('This field is required.'),
  depressionBipolar: string().required('This field is required.'),
  cognitiveDisorder: string().required('This field is required.'),
  muscularDystrophy: string().required('This field is required.'),
  kidneyLiverDisease: string().required('This field is required.'),
  leukemia: string().required('This field is required.'),
  arthritis: string().required('This field is required.'),
  sleepApnea: string().required('This field is required.'),
  none: string().required().when(
    ['alcoholOrDrugAbuse', 'heartDisease', 'stroke', 'lungDisease', 'depressionBipolar', 'cognitiveDisorder', 'muscularDystrophy', 'kidneyLiverDisease', 'leukemia', 'arthritis', 'sleepApnea'],
    ([alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea], schema) => {
      return checkBoxValidation(alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea)
        ? schema.matches(/^true/, 'Please check one checkbox.')
        : string().required('This field is required.');
    }
  ),

  diabetes: string().required('This field is required.'),
  diabetesPriorToAge40: string().when("diabetes", {
    is: "true",
    then: (schema) => schema.when("insuredAge", {
      is: (insuredAge) => insuredAge < 40,
      then: (schema) => schema.required('This field is required.').matches(/^true/, "Invalid answer. Insured is under 40 years of age."),
      otherwise: (schema) => schema.required('This field is required.'),
    }),
    otherwise: (schema) => schema.notRequired()
  }),
  insulinDependent: string().when("diabetes", {
    is: "true",
    then: (schema) => schema.required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  complicationsfromDiabetes: string().when("diabetes", {
    is: "true",
    then: (schema) => schema.required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  aids: string().required('This field is required.'),
  incompleteFollowup: string().required('This field is required.'),
});

function checkBoxValidation(alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea) {
  if (alcoholOrDrugAbuse === "false" && heartDisease === "false" && stroke === "false" && lungDisease === "false" && depressionBipolar === "false" && cognitiveDisorder === "false" && muscularDystrophy === "false" && kidneyLiverDisease === "false" && leukemia === "false" && arthritis === "false" && sleepApnea === "false") { return true; }
  return false;
}

MedicalHistory.DefaultFormState = (prefill = false, mctData = null) => {
  return {
    nicotineUse: prefill ? "Never" : (mctData && mctData.tobacco) || '',
    height: prefill ? '5 ft 10 in' : '',
    weight: prefill ? '175 pounds' : '',
    useRecreationalMarijuana: prefill ? "false" : "",
    marijuanaFrequency: prefill ? "Once a week" : "",
    illegalDrugUse: prefill ? "false" : "",
    illegalPrescriptionDrugUse: prefill ? "false" : "",
    alcoholOrDrugAbuse: "false",
    heartDisease: "false",
    stroke: "false",
    lungDisease: "false",
    depressionBipolar: "false",
    cognitiveDisorder: "false",
    muscularDystrophy: "false",
    kidneyLiverDisease: "false",
    leukemia: "false",
    arthritis: "false",
    sleepApnea: "false",
    none: prefill ? "true" : "false",
    diabetes: prefill ? "false" : "",
    diabetesPriorToAge40: prefill ? "false" : "",
    insulinDependent: prefill ? "false" : "",
    complicationsfromDiabetes: prefill ? "false" : "",
    aids: prefill ? "false" : "",
    incompleteFollowup: prefill ? "false" : "",
  };
};
